import { JSXElementConstructor, ReactElement } from 'react'
import { AxiosError } from 'axios'
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { EmployeeCacheTracker } from '../middleware/hooks/useUserCache'
import { RootState } from '../stateStore'
import { ToastPropsType } from '../types/Components/toastTypes'
import indexedDBHelper from '../utils/indexDBUtils'
import { ValidationError } from '../types/commonTypes'

// Define the async thunk
export const fetchUserCache = createAsyncThunk(
  'app/fetchUserCache',
  async (_, thunkApi) => {
    try {
      return (await indexedDBHelper.getEmployeeCache<EmployeeCacheTracker>(
        indexedDBHelper.defaultCacheName() as string,
      )) as EmployeeCacheTracker
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

export type CachedPage = {
  locationPath: string
  pageNumber: number
  pageSize: number
  range: number[]
}

export type PageConfig = {
  page: number
  size: number
}

type AppState = {
  sidebarShow: boolean
  sidebarUnfoldable: boolean
  asideShow: boolean
  theme: string
  toast:
    | ReactElement<unknown, string | JSXElementConstructor<unknown>>
    | undefined
  toastState: ToastPropsType
  reRenderMenu: boolean
  isSessionExpired: boolean
  persistCurrentPage: number
  basicInfoModalState: boolean
  reRouteLocation: string
  newCustomerPageSize: number
  currentPagination: number[]
  recentPageWithList: CachedPage
  cachedPages: CachedPage[]
  searchString: ''
  pageIsRelated: boolean
  fromRelatedRoute: boolean
  storedCache: PageConfig
  originURL: string
  localizedDateFormat: string

  /**
   * User IndexDB Cache
   */
  currentUserCache: EmployeeCacheTracker
}

type CachedPageConfigType = {
  filteredCache: CachedPage[]
  otherCaches: CachedPage[]
  allCache: CachedPage[]
  origin: string
  recentCache: CachedPage
  isLocationPaged: boolean
  initialCache: CachedPage
  newCustomPageSize: number
  storedCache: PageConfig
}

export type PageDefaultParameterType = {
  initialPage: number
  customPageSize: number
}

type SelectorReturnType = (state: RootState) => CachedPageConfigType

const paginaCurrentPage = 1

const initialState: AppState = {
  sidebarShow: true,
  sidebarUnfoldable: false,
  asideShow: false,
  theme: 'default',
  toast: undefined,
  toastState: { toastColor: '', toastMessage: '' },
  reRenderMenu: true,
  isSessionExpired: false,
  basicInfoModalState: false,
  persistCurrentPage: paginaCurrentPage,
  reRouteLocation: '',
  newCustomerPageSize: 0,
  currentPagination: [],
  recentPageWithList: {
    locationPath: '',
    pageNumber: 1,
    pageSize: 20,
    range: [],
  },
  cachedPages: [],
  searchString: '',
  pageIsRelated: false,
  storedCache: { page: 1, size: 20 },
  fromRelatedRoute: false,
  originURL: '',

  localizedDateFormat: 'DD/MM/YYYY',

  /**
   * User IndexDB Cache
   */
  currentUserCache: {} as EmployeeCacheTracker,
}

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setFromRelatedRoute: (state, action) => {
      state.fromRelatedRoute = action.payload
    },
    setPersistCurrentPage: (state, action) => {
      return { ...state, persistCurrentPage: action.payload }
    },
    clearSelectCurrentPage: (state) => {
      return { ...state, persistCurrentPage: 1 }
    },
    addToast: (state, action) => {
      return { ...state, toast: action.payload }
    },
    addToastState: (state, action) => {
      return { ...state, toastState: action.payload as ToastPropsType }
    },
    toggleSidebar: (state) => {
      state.sidebarShow = !state.sidebarShow
    },
    toggleFoldable: (state) => {
      state.sidebarUnfoldable = !state.sidebarUnfoldable
    },
    setReRenderMenu: (state, action) => {
      return { ...state, reRenderMenu: action.payload }
    },
    setIsSessionExpired: (state, action) => {
      return { ...state, isSessionExpired: action.payload }
    },
    setBasicInfoModalState: (state, action) => {
      return { ...state, basicInfoModalState: action.payload }
    },
    setCurrentPagination: (state, action) => {
      return {
        ...state,
        currentPagination: action.payload,
      }
    },
    setReRouteLocation: (state, action) => {
      return { ...state, reRouteLocation: action.payload }
    },
    setRecentPageWithList: (state, action) => {
      return { ...state, recentPageWithList: action.payload }
    },
    setCachedPages: (state, action) => {
      return { ...state, cachedPages: action.payload }
    },
    setCachedPageSize: (state, action) => {
      return {
        ...state,
        newCustomerPageSize: action.payload,
        recentPageWithList: {
          ...state.recentPageWithList,
          pageNumber: 1,
          pageSize: action.payload,
        },
      }
    },
    setSearchString: (state, action) => {
      return { ...state, searchString: action.payload }
    },
    setPageIsRelated: (state, action) => {
      return { ...state, pageIsRelated: action.payload }
    },
    setStoredCache: (state, action) => {
      return { ...state, storedCache: action.payload }
    },
    setOriginUrl: (state, action) => {
      return { ...state, originURL: action.payload }
    },
    setLocalizedDateFormat: (state, action) => {
      return { ...state, localizedDateFormat: action.payload }
    },

    /**
     * User IndexDB Cache
     */
    setCurrentUserCache: (state, action) => {
      return { ...state, currentUserCache: action.payload }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserCache.fulfilled, (state, action) => {
      state.currentUserCache = action.payload
    })
  },
})

const selectFromRelatedRoute = (state: RootState): boolean =>
  state.app.fromRelatedRoute

const selectIsSessionExpired = (state: RootState): boolean =>
  state.app.isSessionExpired
const selectBasicInfoModalState = (state: RootState): boolean =>
  state.app.basicInfoModalState
const selectCurrentPage = (state: RootState): number =>
  state.app.persistCurrentPage

const getSidebarShow = (state: RootState): boolean => state.app.sidebarShow

const selectCurrentPagination = (state: RootState): number[] =>
  state.app.currentPagination

const selectReRouteOrigin = (state: RootState): string =>
  state.app.reRouteLocation

const selectNewCustomPageSize = (state: RootState): number =>
  state.app.newCustomerPageSize

const selectRecentPageWithList = (state: RootState): CachedPage =>
  state.app.recentPageWithList

const selectAllCachedPage = (state: RootState): CachedPage[] =>
  state.app.cachedPages

const selectStoredCache = (state: RootState): PageConfig =>
  state.app.storedCache

const getSearchString = (state: RootState): string => state.app.searchString

const getHasPageRelation = (state: RootState): boolean =>
  state.app.pageIsRelated

const getOriginUrl = (state: RootState): string => state.app.originURL

const getLocalizedDateFormat = (state: RootState): string =>
  state.app.localizedDateFormat

/**
 * User IndexDB Cache
 */
const getCurrentUserCache = (state: RootState): EmployeeCacheTracker =>
  state.app.currentUserCache

const selectAllConfig = (
  state: RootState,
  location: string,
  expectedOrigin: string[],
  defaultParam: PageDefaultParameterType,
): CachedPageConfigType => {
  const filtered = state.app.cachedPages?.filter(
    (cache) => cache?.locationPath === location,
  )
  const nullCached: CachedPage = {
    locationPath: '',
    pageNumber: defaultParam.initialPage,
    pageSize: defaultParam.customPageSize,
    range: [],
  }

  return {
    filteredCache: filtered,
    otherCaches: state.app.cachedPages?.filter(
      (cache) => cache?.locationPath !== location && cache.locationPath,
    ),
    allCache: state.app.cachedPages,
    origin: state.app.reRouteLocation,
    recentCache: state.app.recentPageWithList,
    isLocationPaged:
      state.app.reRouteLocation === '' && expectedOrigin.includes(location),
    initialCache: filtered.length ? filtered[0] : nullCached,
    newCustomPageSize: state.app.newCustomerPageSize,
    storedCache: state.app.storedCache,
  }
}

const selectCachedPageConfig = (
  location: string,
  expectedOrigin: string[],
  defaultParam: PageDefaultParameterType,
): SelectorReturnType =>
  createSelector(
    [
      (state: RootState) =>
        appSelectors.selectAllConfig(
          state,
          location,
          expectedOrigin,
          defaultParam,
        ),
    ],
    (config) => config,
  )

export const appSelectors = {
  selectFromRelatedRoute,
  selectIsSessionExpired,
  selectCurrentPage,
  selectCurrentPagination,
  selectReRouteOrigin,
  selectNewCustomPageSize,
  selectRecentPageWithList,
  selectAllCachedPage,
  selectAllConfig,
  selectBasicInfoModalState,
  selectCachedPageConfig,
  selectStoredCache,
  getSearchString,
  getHasPageRelation,
  getSidebarShow,
  getOriginUrl,
  getLocalizedDateFormat,

  /**
   * User IndexDB Cache
   */
  getCurrentUserCache,
}

const appThunk = {
  fetchUserCache,
}

export const appService = {
  ...appThunk,
  actions: appSlice.actions,
  selectors: appSelectors,
}

export default appSlice.reducer
